import { PromiseExtended } from 'dexie';
import { organizationInDtoToEntity } from 'shared/domain/organization/mapping/toEntity';
import { OrganizationInDto } from 'shared/domain/organization/types/dto';
import { OrganizationEntity } from 'shared/domain/organization/types/entity';
import { Changes } from 'shared/types/commonEntities';
import { LogLevel } from 'shared/types/logger';
import {
  makeDefaultAddBatch,
  makeDefaultAddBatchMapped,
  makeDefaultGetMany,
  makeDefaultGetOne,
  makeReplaceAllBatchMapped,
} from '../defaultDaoFactories';
import { Operation, getErrorMsg, logWithRethrow } from '../helpers';
import db from '../index';
import { wrapQuery } from '../queryWrapper';
import { entityDbClearFactory } from '../entityDbClearFactory';
import { clear as organizationsServiceClear } from './organizationsService';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { organizationContract } from 'shared/domain/organization/organizationContract';

/** The rows with the download state: the state left over an empty table would keep the collection "downloaded". */
export const clear = entityDbClearFactory(
  db,
  ['organizations', 'organizationsService'],
  () => db.organizations.clear(),
  organizationsServiceClear,
  makeClearPublisher(organizationContract)
);

export const getOne = makeDefaultGetOne<string, OrganizationEntity>(
  db,
  db.organizations,
  '_id'
);

export const get = makeDefaultGetMany<OrganizationEntity>(
  db,
  db.organizations
);

/** The downloaded list replaces the stored one: an organization the user no longer belongs to is dropped. */
export const addBatch = makeReplaceAllBatchMapped<
  OrganizationInDto,
  OrganizationEntity
>(db, db.organizations, organizationInDtoToEntity);

/** Stores the organizations the server returned for a change, next to the others. */
export const updateBatch = makeDefaultAddBatchMapped<
  OrganizationInDto,
  OrganizationEntity
>(db, db.organizations, organizationInDtoToEntity);

export const updateLocalBatch = makeDefaultAddBatch<OrganizationEntity>(
  db,
  db.organizations
);

export const updateOne = wrapQuery(
  db,
  (
    organizationId: string,
    changes: Changes<OrganizationEntity>
  ): PromiseExtended => {
    return db.organizations.update(organizationId, changes).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.updateOne, 'organizations'),
        errorObj: e,
        additionalInfo: { query: { data: { organizationId, changes } } },
      })
    );
  }
);
